import { BellIcon } from 'core/components/Icons'
import { Badge } from 'core/components/ui/Badge'
import { FilterBar } from 'core/components/ui/FilterBar'
import { PageHeader } from 'core/components/ui/PageHeader'
import { Table } from 'core/components/ui/Table'
import { AdminNotificationItem, notificationsApi } from 'features/notifications/data/notificationsApi'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

export function NotificationsPage() {
  const [logs, setLogs] = useState<AdminNotificationItem[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const limit = 10

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true)
      const res = await notificationsApi.getNotifications({
        skip: (page - 1) * limit,
        limit,
        type: categoryFilter || undefined,
      })
      setLogs(res.data || [])
      setTotal(res.total || 0)
    } catch (err) {
      // NOSONAR: Logging notification query failure for client diagnostics
      // eslint-disable-next-line no-console
      console.error('Failed to load notifications:', err) // NOSONAR
    } finally {
      setLoading(false)
    }
  }, [page, limit, categoryFilter])

  useEffect(() => {
    loadNotifications()
  }, [loadNotifications])

  // Filter logs based on search query
  const filteredLogs = logs.filter((log) => {
    if (!search) return true
    const s = search.toLowerCase()
    return (
      log.title.toLowerCase().includes(s) ||
      log.message.toLowerCase().includes(s) ||
      (log.id && log.id.toLowerCase().includes(s)) ||
      (log.data?.indent_id && String(log.data.indent_id).toLowerCase().includes(s)) ||
      (log.data?.payment_id && String(log.data.payment_id).toLowerCase().includes(s)) ||
      (log.data?.grievance_id && String(log.data.grievance_id).toLowerCase().includes(s)) ||
      (log.data?.user && String(log.data.user).toLowerCase().includes(s))
    )
  })

  // Pagination calculations
  const totalItems = total || filteredLogs.length
  const totalPages = Math.max(1, Math.ceil(totalItems / limit))

  // Columns definition
  const columns = [
    {
      key: 'id',
      label: 'Notification ID',
      render: (r: AdminNotificationItem) => (
        <span className="font-mono text-xs font-semibold text-text-primary">
          {r.id.length > 12 ? `${r.id.slice(0, 10)}...` : r.id}
        </span>
      ),
    },
    {
      key: 'timestamp',
      label: 'Timestamp',
      render: (r: AdminNotificationItem) => (
        <span className="text-xs text-text-secondary">
          {new Date(r.created_at).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })}
        </span>
      ),
    },
    {
      key: 'operator',
      label: 'Triggered By',
      render: (r: AdminNotificationItem) => {
        const user = String(r.data?.user || r.data?.business_name || r.data?.payer || r.data?.raised_by || 'Customer')
        const role = String(r.data?.role || 'External User')
        return (
          <div className="text-xs text-left">
            <p className="font-bold text-text-primary">{user}</p>
            <span className="text-[10px] text-text-secondary font-medium uppercase tracking-wider">{role}</span>
          </div>
        )
      },
    },
    {
      key: 'category',
      label: 'Category',
      render: (r: AdminNotificationItem) => {
        const colors: Record<string, 'primary' | 'success' | 'warning' | 'danger' | 'info'> = {
          order_status: 'primary',
          kyc_status: 'warning',
          payment_status: 'success',
          grievance: 'danger',
          general: 'info',
        }
        const labels: Record<string, string> = {
          order_status: 'Orders',
          kyc_status: 'KYC',
          payment_status: 'Payments',
          grievance: 'Grievances',
          general: 'System',
        }
        return <Badge tone={colors[r.type] || 'info'}>{labels[r.type] || r.type}</Badge>
      },
    },
    {
      key: 'description',
      label: 'Notification Details',
      render: (r: AdminNotificationItem) => (
        <div className="text-left max-w-md">
          <p className="text-xs font-bold text-text-primary">{r.title}</p>
          <p className="text-[11px] text-text-secondary leading-normal">{r.message}</p>
        </div>
      ),
    },
    {
      key: 'refId',
      label: 'Reference Action',
      render: (r: AdminNotificationItem) => {
        const refId = r.data?.indent_id || r.data?.payment_id || r.data?.grievance_id || r.data?.user_id
        if (r.action_url) {
          return (
            <Link
              to={r.action_url}
              className="font-mono text-xs font-bold text-primary hover:underline hover:text-primary-hover flex items-center gap-1"
            >
              {refId ? String(refId) : 'View Details'}
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </Link>
          )
        }

        return <span className="text-xs text-text-secondary font-mono">-</span>
      },
    },
  ]

  const isFilterActive = search !== '' || categoryFilter !== ''

  const handleResetFilters = () => {
    setSearch('')
    setCategoryFilter('')
    setPage(1)
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Notification History Ledger"
        description="Monitor system alerts, KYC statuses, B2B order placements, and resolution tracking."
        breadcrumbs={[{ label: 'Portal', path: '/' }, { label: 'Notifications' }]}
      />

      {/* Filter Bar */}
      <FilterBar
        searchPlaceholder="Search triggered by, details, ref ID..."
        searchValue={search}
        onSearchChange={(val) => {
          setSearch(val)
          setPage(1)
        }}
        showReset={isFilterActive}
        onReset={handleResetFilters}
      >
        {/* Category selector */}
        <select
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value)
            setPage(1)
          }}
          className="px-3 py-1.5 text-xs font-semibold rounded-btn border border-border bg-surface text-text-primary focus:border-primary focus:outline-none transition-colors"
        >
          <option value="">All Categories</option>
          <option value="order_status">Orders</option>
          <option value="kyc_status">KYC</option>
          <option value="payment_status">Payments</option>
          <option value="grievance">Grievances</option>
        </select>
      </FilterBar>

      {/* Logs Table */}
      <div className="bg-surface rounded-card border border-border overflow-hidden">
        <Table
          columns={columns}
          data={filteredLogs}
          loading={loading}
          pagination={{
            currentPage: page,
            totalPages,
            onPageChange: setPage,
            totalItems,
          }}
          emptyMessage="No notifications match your filter options."
          emptyIcon={<BellIcon size={20} className="opacity-60" />}
        />
      </div>
    </div>
  )
}
